import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import type { ActiveFlag } from '@hcc/types';
import { useAppendEvent, usePerson } from '../../hooks/usePeople';
import { ApiError } from '../../lib/api';
import { Button } from '../ui/Button';
import { FlagBanner } from './FlagBanner';

/**
 * Warning pill chip + dialog. Tapping the chip opens a list of the
 * person's active flag reasons with a per-flag Clear action.
 * Self-fetches the person's projected state when opened so the chip
 * itself doesn't pay any data cost until tapped.
 *
 * Renders nothing when count is 0.
 */
export function FlagsButton({
  personId,
  personName,
  count,
}: {
  personId: string;
  personName: string;
  count: number;
}) {
  const [open, setOpen] = useState(false);
  if (count <= 0) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        title={`${count} active flag${count === 1 ? '' : 's'} — tap to view`}
        className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning-soft/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-soft-fg transition-colors hover:bg-warning-soft/25"
      >
        <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />
        Flag{count === 1 ? '' : 's'}
        {count > 1 ? <span className="tabular-nums">· {count}</span> : null}
      </button>
      {open ? (
        <FlagsDialog
          personId={personId}
          personName={personName}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function FlagsDialog({
  personId,
  personName,
  onClose,
}: {
  personId: string;
  personName: string;
  onClose: () => void;
}) {
  const personQ = usePerson(personId);
  const append = useAppendEvent(personId);
  const flags = personQ.data?.state.flags ?? [];

  async function clearFlag(f: ActiveFlag) {
    try {
      await append.mutateAsync({
        event_type: 'FlagCleared',
        payload: { flag_event_id: f.event_id },
      } as Parameters<typeof append.mutateAsync>[0]);
      toast.success('Flag cleared');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not clear flag');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Flags for ${personName}`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 border-b border-divider/10 bg-warning-soft/15 px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle
              className="h-4 w-4 text-warning"
              strokeWidth={2.5}
            />
            <h2 className="truncate text-base font-semibold text-warning-soft-fg">
              Flags — {personName}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {personQ.isLoading ? (
            <div className="text-sm text-fg-muted">Loading flags…</div>
          ) : flags.length === 0 ? (
            <div className="text-sm text-fg-muted">
              No active flags. They may have been cleared from another screen.
            </div>
          ) : (
            <FlagBanner flags={flags} onClearClick={clearFlag} />
          )}
        </div>
      </div>
    </div>
  );
}
